import React from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatedCurrency } from '@hub/components/animated-currency';
import WithdrawFunds from '@hub/features/withdraw-funds-nova/withdraw-funds';
import { Box, useTheme } from '@mui/material';
import { SystemIcon } from '@monorepo/nova-icons';
import { Typography } from '@monorepo/nova-ui';
import { Account, AccountType, WithdrawRulesResponse } from '@monorepo/types';
import { PayoutSource } from '@monorepo/types';
import { DEFAULT_VALUES } from '../constants/objective-constants';
import { ObjectiveData } from '../types';
import { getMainTextStyle } from '../utils/styles';

interface PayoutObjectiveProps {
  account?: Account;
  eligibilityData: WithdrawRulesResponse | undefined;
}

export const PayoutObjective = ({
  account,
  eligibilityData,
}: PayoutObjectiveProps): ObjectiveData => {
  const { t } = useTranslation('dashboardHeader');
  const theme = useTheme();

  const primaryLabel = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(1),
      }}
    >
      <Typography
        component="span"
        variant="body2"
        sx={{ color: theme.palette.text.second }}
      >
        {account?.type === AccountType.Funded
          ? t('objectives.profit.fundedLabel')
          : t('objectives.profit.label')}
      </Typography>
    </Box>
  );

  return {
    icon: (
      <SystemIcon iconName="Coins" size={DEFAULT_VALUES.OBJECTIVE_ICON_SIZE} />
    ),
    primaryText: primaryLabel,
    secondaryText: (
      <>
        <Box>
          <AnimatedCurrency
            value={Math.max(0, eligibilityData?.availableAmount ?? 0)}
            fontSize={20}
            textColor={
              getMainTextStyle(theme)?.color || theme.palette.text.primary
            }
            compact={true}
            currencySymbol="$"
            showAnimation={true}
            fontStyle="normal"
            letterSpacing="0.03px"
          />
        </Box>
        {eligibilityData && account?.type === AccountType.Funded && (
          <WithdrawFunds
            sourceId={account?._id}
            source={PayoutSource.Account}
            displayName={account?.tsAccount || account?.tsAccountId || ''}
            tsAccountId={account?.tsAccountId}
            eligibilityData={eligibilityData}
            account={account}
          />
        )}
      </>
    ),
  };
};
